import type { JSX, ReactNode } from 'react'
import { useEffect, useMemo } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Activity, Gauge, Plus, Radio, Server as ServerIcon, TriangleAlert } from 'lucide-react'
import { BOT_STATES, wsRoom } from '@apiary/shared'
import type { BotState } from '@apiary/shared'
import type { BotDto, BotEventDto, StateCountDto } from '@/lib/dto'
import { cn } from '@/lib/cn'
import { isOnServer } from '@/lib/format'
import { clockOf, megabytesOf } from '@/ui/format'
import { useBots, useDashboardSummary, useRecentEvents } from '@/hooks/queries'
import { useWsRoom } from '@/hooks/useWsRoom'
import { useMinimaps } from '@/hooks/useMinimap'
import { useCanOperate, useSessionStore } from '@/stores/session'
import { averagePing, useTelemetryStore } from '@/stores/telemetry'
import { stagger, useCountUp } from '@/lib/motion'
import { Button } from '@/ui/primitives/Button'
import { Chip } from '@/ui/primitives/Chip'
import { Panel, SectionHead, Surface } from '@/ui/primitives/Surface'
import { SkinHead } from '@/ui/primitives/SkinHead'
import { StatusDot } from '@/ui/primitives/StatusDot'
import { Sparkline } from '@/ui/charts/Sparkline'
import { HourTracksChart } from '@/ui/charts/HourTracksChart'
import { StateBreakdown } from '@/ui/charts/StateBreakdown'
import { Meter } from '@/ui/charts/Meter'
import { seriesColor } from '@/ui/charts/chart-tokens'
import { BotTile } from '@/screens/bots/BotTile'
import { useBotCommand } from '@/hooks/useBotCommands'
import { mergeHourTracks } from './day-tracks'
import { eventKindLabel, hasKindLabel } from './event-kinds'

/** Сколько плиток ботов показывает дашборд: остальное — на «Все боты». */
const DASHBOARD_TILES = 8

/**
 * Дашборд: состояние флота одним экраном.
 *
 * Порядок сверху вниз — по частоте вопросов, с которыми сюда заходят:
 * «сколько живо и как связь» (плитки), «кто именно и что с ними» (состав флота),
 * «что было за сутки» (графики), «на чём это всё крутится» (runner'ы), потом
 * сами боты и лента событий.
 *
 * Данные для спарклайнов копит клиент (`stores/telemetry`): у сервера истории
 * «ping минуту назад» нет, а заводить под неё таблицу ради двух линий дорого.
 * После перезагрузки страницы линии начинаются заново — это честно.
 */
export function DashboardScreen(): JSX.Element {
  const navigate = useNavigate()
  const orgId = useSessionStore(state => state.activeOrgId)
  useWsRoom(orgId === null ? null : wsRoom.org(orgId))

  const summary = useDashboardSummary()
  const bots = useBots()
  const recent = useRecentEvents()
  const canOperate = useCanOperate()
  const command = useBotCommand()

  const items = bots.data?.items ?? []
  const sample = useTelemetryStore(state => state.sample)
  const samples = useTelemetryStore(state => state.samples)

  const playingNow =
    summary.data?.botsPlaying ?? items.filter(bot => bot.state === 'PLAYING').length
  const pingNow = useMemo(
    () =>
      averagePing(
        items.filter(bot => bot.state === 'PLAYING').map(bot => bot.lastDetail?.ping ?? null)
      ),
    [items]
  )

  useEffect(() => {
    if (summary.data === undefined) return
    sample({ playing: playingNow, avgPing: pingNow })
  }, [sample, summary.data, playingNow, pingNow])

  const data = summary.data
  const breakdown = useMemo(() => normalizeBreakdown(data?.stateBreakdown, items), [data, items])
  const playingSeries = samples.map(entry => entry.playing)
  const pingSeries = samples.map(entry => entry.avgPing)
  const uptime = data?.uptimePct24h ?? null
  const runners = data?.runners ?? []
  const runnersOnline = runners.filter(runner => runner.online).length

  // На дашборде показываются первые восемь плиток — их и держим.
  const shown = useMemo(() => items.slice(0, DASHBOARD_TILES), [items])

  /**
   * Мини-карты плиток. Раньше их тут не было вовсе: дашборд рендерил `BotTile`
   * без кадра, и карта вечно оставалась в состоянии «появится, когда прогрузятся
   * чанки», хотя на экране ботов работала. Кадр приходит в комнату `bot:<id>`, и
   * ПОДПИСКА на неё — это и есть включатель среза в runner'е
   * (`browser.gateway.afterRoomJoin` → `setMinimapWanted`). Нет подписки — никто
   * ничего и не считает.
   *
   * Только у играющих: у остальных срез считать нечего, а подписка включила бы
   * его в runner'е (docs/08 §1). Только у видимых плиток: подписываться за
   * невидимый девятый ряд — платить трафиком за то, чего не показываем.
   */
  const minimaps = useMinimaps(
    useMemo(() => shown.filter(bot => isOnServer(bot.state)).map(bot => bot.id), [shown])
  )

  const dayPoints = useMemo(
    () => mergeHourTracks(data?.uptimeHours24, data?.incidentsHours24),
    [data]
  )

  /** Имя и скин бота по id — лента событий приходит только с `botId`. */
  const botsById = useMemo(() => new Map(items.map(bot => [bot.id, bot])), [items])

  return (
    <>
      <SectionHead
        title="Сводка"
        actions={
          <Button
            variant="primary"
            disabled={!canOperate}
            onClick={() => void navigate({ to: '/bots/new' })}
          >
            <Plus aria-hidden className="size-4" />
            Создать бота
          </Button>
        }
      />

      {summary.isPending ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_unused, index) => (
            <Surface key={index} variant="raised" className="shimmer h-28" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            index={0}
            label="Онлайн"
            icon={<Activity aria-hidden className="size-4" />}
            tone={playingNow === items.length && items.length > 0 ? 'live' : 'warn'}
            value={`${playingNow}/${data?.botsTotal ?? items.length}`}
            count={playingNow}
            spark={{ points: playingSeries, color: 'var(--color-live)', label: 'Ботов в игре' }}
          />
          <StatCard
            index={1}
            label="Uptime 24 ч"
            icon={<Gauge aria-hidden className="size-4" />}
            tone={uptime === null ? 'idle' : uptime >= 99 ? 'live' : uptime >= 80 ? 'warn' : 'fail'}
            value={uptime === null ? '—' : `${uptime.toFixed(1)}%`}
            footnote="доля времени в игре"
          />
          <StatCard
            index={2}
            label="Инциденты"
            icon={<TriangleAlert aria-hidden className="size-4" />}
            tone={(data?.incidents24h ?? 0) > 0 ? 'warn' : 'idle'}
            value={String(data?.incidents24h ?? 0)}
            count={data?.incidents24h ?? 0}
            footnote="падения и реконнекты"
          />
          <StatCard
            index={3}
            label="Ping флота"
            icon={<Radio aria-hidden className="size-4" />}
            tone="honey"
            value={pingNow === null ? '—' : `${pingNow} мс`}
            spark={{ points: pingSeries, color: seriesColor('1'), label: 'Средний ping' }}
            footnote="среднее по играющим"
          />
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-3 xl:items-start">
        <div className="flex min-w-0 flex-col gap-4">
          <Panel title="Состав флота" note={`${items.length} всего`}>
            <StateBreakdown
              items={breakdown}
              onPick={state => void navigate({ to: '/bots', search: { state } })}
            />
          </Panel>

          {runners.length === 0 ? null : (
            <Panel
              title="Runner'ы"
              note={`${runnersOnline}/${runners.length} на связи`}
              bodyClassName="flex flex-col gap-4"
            >
              {runners.map(runner => (
                <div key={runner.id} className="flex flex-col gap-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-2 font-mono text-2xs text-ink">
                      <ServerIcon aria-hidden className="size-3.5 text-ink-faint" />
                      <span className="truncate">{runner.name}</span>
                    </span>
                    <span
                      className={cn(
                        'flex items-center gap-1.5 text-2xs',
                        runner.online ? 'text-live' : 'text-fail'
                      )}
                    >
                      <StatusDot tone={runner.online ? 'live' : 'fail'} />
                      {runner.online ? 'на связи' : 'нет связи'}
                    </span>
                  </div>
                  <Meter
                    label="Боты"
                    value={runner.load?.bots ?? 0}
                    max={runner.maxBots}
                    text={`${runner.load?.bots ?? 0} / ${runner.maxBots}`}
                  />
                  {/* cpu приходит долей 0..1, а не процентами — max тут 1. */}
                  <Meter
                    label="CPU"
                    value={runner.load?.cpu ?? 0}
                    max={1}
                    text={`${Math.round((runner.load?.cpu ?? 0) * 100)}%`}
                  />
                  <Meter
                    label="Память"
                    value={runner.load?.rssMb ?? 0}
                    max={2048}
                    text={megabytesOf(runner.load?.rssMb ?? 0)}
                  />
                  <p className="font-mono text-2xs text-ink-muted">{runner.version}</p>
                </div>
              ))}
            </Panel>
          )}
        </div>

        {/* Одна ось часов на обе величины, но шкалы РАЗНЫЕ и подписаны каждая:
            проценты и штуки на общей шкале врали бы о пропорциях (docs/06 §9). */}
        <Panel title="Сутки" note="по часам, UTC" className="xl:col-span-2">
          {summary.isPending ? (
            <div className="shimmer h-52 rounded-sm" />
          ) : dayPoints.length === 0 ? (
            <p className="py-12 text-center text-base text-ink-muted">
              Почасовой статистики пока нет — она появится через час после запуска панели.
            </p>
          ) : (
            <HourTracksChart
              points={dayPoints}
              label="Uptime и инциденты по часам за последние сутки, UTC"
              formatKind={eventKindLabel}
            />
          )}
        </Panel>
      </div>

      <SectionHead
        title="Боты"
        actions={
          <Link
            to="/bots"
            className="press rounded-sm px-2 py-1 text-2xs text-ink-muted hover:text-ink"
          >
            Все боты →
          </Link>
        }
      />

      {bots.isPending ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {Array.from({ length: 4 }, (_unused, index) => (
            <Surface key={index} variant="raised" className="shimmer h-72" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Surface variant="raised" className="px-6 py-12 text-center">
          <p className="font-display text-md font-semibold text-ink">Ботов пока нет</p>
          <p className="mt-1 text-base text-ink-muted">
            Создайте первого: понадобится Minecraft-аккаунт и профиль сервера.
          </p>
          <Button
            variant="primary"
            className="mt-4"
            disabled={!canOperate}
            onClick={() => void navigate({ to: '/bots/new' })}
          >
            <Plus aria-hidden className="size-4" />
            Создать бота
          </Button>
        </Surface>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {shown.map((bot, index) => (
            <div key={bot.id} className="anim-rise" style={stagger(index)}>
              <BotTile
                bot={bot}
                canOperate={canOperate}
                busy={command.isPending}
                minimap={minimaps[bot.id] ?? null}
                onStart={() =>
                  command.mutate({ botId: bot.id, botName: bot.name, command: 'start' })
                }
                onStop={() => command.mutate({ botId: bot.id, botName: bot.name, command: 'stop' })}
                onRestart={() =>
                  command.mutate({ botId: bot.id, botName: bot.name, command: 'restart' })
                }
                onEdit={() => void navigate({ to: '/bots/$botId', params: { botId: bot.id } })}
                onDelete={() => void navigate({ to: '/bots/$botId', params: { botId: bot.id } })}
                onCamera={() => void navigate({ to: '/bots/$botId', params: { botId: bot.id } })}
              />
            </div>
          ))}
        </div>
      )}

      <Panel title="Последние события" note={`${(recent.data ?? []).length}`} bodyClassName="p-0">
        {recent.isPending ? (
          <div className="shimmer h-24" />
        ) : (recent.data ?? []).length === 0 ? (
          <p className="px-4 py-8 text-center text-base text-ink-muted">Событий пока нет.</p>
        ) : (
          <ul className="divide-y divide-line">
            {(recent.data ?? []).map(event => (
              <EventRow key={event.id} event={event} bot={botsById.get(event.botId) ?? null} />
            ))}
          </ul>
        )}
      </Panel>
    </>
  )
}

/**
 * Строка ленты «Последние события».
 *
 * Раньше здесь были время, сырой `kind` и текст — по такой строке нельзя было
 * понять, У КАКОГО бота проблема, а это первый вопрос при взгляде на ленту.
 * Теперь слева голова бота и его имя ссылкой на карточку, а вид события —
 * по-человечески; сырой код остаётся вторым планом (по нему фильтруют логи).
 *
 * Бот ищется в уже загруженном списке: лента приходит по WS только с `botId`, а
 * второй запрос ради имени на каждую строку не нужен. Бота могли удалить или он
 * не попал в текущую страницу списка — тогда показываем id, но строку не прячем.
 */
function EventRow({ event, bot }: { event: BotEventDto; bot: BotDto | null }): JSX.Element {
  const known = hasKindLabel(event.kind)
  return (
    <li className="anim-slide-in flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 transition-colors duration-140 hover:bg-panel-2">
      {/* `ink-muted`, а не `ink-faint`: время — текст, а faint даёт 3.6:1 и по
          docs/10 §6 разрешён только декору и крупным иконкам. */}
      <span className="font-mono text-2xs text-ink-muted tabular-nums">{clockOf(event.at)}</span>

      {bot === null ? (
        <span className="font-mono text-2xs text-ink-muted">{event.botId}</span>
      ) : (
        <Link
          to="/bots/$botId"
          params={{ botId: bot.id }}
          className="press flex min-w-0 items-center gap-2 rounded-xs text-2xs text-ink hover:text-honey"
        >
          {/* Голова не крутится: в ленте это десяток аватарок, и постоянное
              движение в списке, который читают глазами, только мешает. */}
          <SkinHead
            uuid={bot.mcAccountUuid}
            name={bot.lastDetail?.username ?? bot.mcAccountLabel}
            size={20}
            spin={false}
          />
          <span className="truncate font-medium">{bot.name}</span>
        </Link>
      )}

      <Chip
        tone={event.level === 'ERROR' ? 'fail' : event.level === 'WARN' ? 'warn' : 'neutral'}
        dot={event.level === 'ERROR' || event.level === 'WARN'}
        title={known ? `код события: ${event.kind}` : undefined}
      >
        {eventKindLabel(event.kind)}
      </Chip>

      <span className="min-w-0 flex-1 font-mono text-2xs text-ink-muted">{event.message}</span>
    </li>
  )
}

/** Плитка сводки: число, форма ряда и сноска. */
function StatCard({
  index,
  label,
  value,
  count,
  icon,
  tone,
  spark,
  footnote
}: {
  index: number
  label: string
  value: string
  count?: number
  icon: ReactNode
  tone: 'live' | 'warn' | 'fail' | 'idle' | 'honey'
  spark?: { points: Array<number | null>; color: string; label: string }
  footnote?: string
}): JSX.Element {
  // Агрегат «доезжает» до нового значения: движение сообщает, что показатель
  // изменился. Мгновенные значения (ping) так не гоняем — их анимировать нельзя.
  const animated = useCountUp(count ?? 0)
  const shown =
    count === undefined ? value : value.replace(String(count), String(Math.round(animated)))

  const VALUE_TONE = {
    live: 'text-live',
    warn: 'text-warn',
    fail: 'text-fail',
    idle: 'text-ink',
    honey: 'text-honey'
  } as const

  const ICON_TONE = {
    live: 'bg-live/12 text-live',
    warn: 'bg-warn/12 text-warn',
    fail: 'bg-fail/12 text-fail',
    idle: 'bg-panel-3 text-ink-muted',
    honey: 'bg-honey/12 text-honey'
  } as const

  return (
    <Surface variant="raised" className="anim-rise flex flex-col gap-3 p-4" style={stagger(index)}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-2xs tracking-wide text-ink-muted uppercase">{label}</span>
        <span
          className={cn(
            'flex size-7 shrink-0 items-center justify-center rounded-sm',
            ICON_TONE[tone]
          )}
        >
          {icon}
        </span>
      </div>

      <p
        className={cn(
          'font-display text-xl leading-none font-semibold tabular-nums',
          VALUE_TONE[tone]
        )}
      >
        {shown}
      </p>

      {spark === undefined ? null : (
        <Sparkline points={spark.points} color={spark.color} label={spark.label} height={26} />
      )}
      {footnote === undefined ? null : (
        <p className="truncate font-mono text-2xs text-ink-muted">{footnote}</p>
      )}
    </Surface>
  )
}

/**
 * Состав флота. Берём агрегат сервера, а если старый API его не отдал —
 * считаем по списку: дашборд не должен пустеть из-за версии бэкенда.
 */
function normalizeBreakdown(
  breakdown: StateCountDto[] | undefined,
  bots: BotDto[]
): StateCountDto[] {
  if (breakdown !== undefined && breakdown.length > 0) return breakdown
  const counts = new Map<BotState, number>()
  for (const bot of bots) counts.set(bot.state, (counts.get(bot.state) ?? 0) + 1)
  return BOT_STATES.map(state => ({ state, count: counts.get(state) ?? 0 }))
}
